import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { ClientsDataContext } from "@shared/features/common/ClientsData.context";
import { sharedLib } from "@shared/services/shared.service";
import {
  ClientData,
  ClientStatus,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import { INACTIVE_ACCOUNT_STATUSES } from "@shared/features/portfolio/constants";
import { getIsMicaAccount } from "@shared/features/configuration/utils";

const ClientsDataProvider = ({ children }: PropsWithChildren) => {
  const getClientsListStatus =
    sharedLib.sharedApi.configuration.useGetClientsListQuery(undefined);
  const { data: clientsData, isSuccess } = getClientsListStatus;

  const [accountIdToAccount, accountNameToAccount] = useMemo(() => {
    let clientIdToClient: Record<number, ClientData> = {};
    let clientNameToClient: Record<string, ClientData> = {};
    clientsData?.forEach((client) => {
      clientIdToClient[client.client_id] = client;
      clientNameToClient[client.client_name] = client;
    });
    return [clientIdToClient, clientNameToClient];
  }, [clientsData]);

  const notDeletedClientsData = useMemo(
    () =>
      clientsData?.filter(
        (client) => client.client_status !== ClientStatus.DELETED
      ),
    [clientsData]
  );

  const activeClientsData = useMemo(
    () =>
      notDeletedClientsData?.filter(
        (client) => client.client_status === ClientStatus.ACTIVE
      ) || [],
    [notDeletedClientsData]
  );

  const writableActiveClientsData = useMemo(
    () =>
      activeClientsData?.filter(
        (client) =>
          client.permission_level !== UserImpersonationPermissionLevel.READ
      ),
    [activeClientsData]
  );

  const getClientIds = (clients?: ClientData[]) => {
    return clients?.map((client) => client.client_id) || [];
  };

  const clientIds = useMemo(() => getClientIds(clientsData), [clientsData]);

  const activeClientIds = useMemo(
    () => getClientIds(activeClientsData),
    [activeClientsData]
  );

  const clientExchanges = useMemo(
    () => clientsData?.map((client) => client.exchange) || [],
    [clientsData]
  );

  const getAccountById = useCallback(
    (accountId?: number): ClientData | undefined => {
      if (!accountId) return;
      return accountIdToAccount?.[accountId];
    },
    [accountIdToAccount]
  );

  const getAccountExchangeById = (accountId: number) => {
    return getAccountById(accountId)?.exchange;
  };

  const getAccountByName = useCallback(
    (accountName: string): ClientData | undefined => {
      return accountNameToAccount?.[accountName];
    },
    [accountNameToAccount]
  );

  const getAccountStatus = useCallback(
    (accountId?: number) => {
      if (!accountId) return;
      return getAccountById(accountId)?.client_status;
    },
    [getAccountById]
  );

  const getIsAccountInvalid = useCallback(
    (accountId?: number) => {
      return getAccountStatus(accountId) === ClientStatus.INVALID;
    },
    [getAccountStatus]
  );

  const getIsAccountInactive = useCallback(
    (accountId?: number) => {
      const status = getAccountStatus(accountId);
      return INACTIVE_ACCOUNT_STATUSES.has(status!);
    },
    [getAccountStatus]
  );

  const getIsMicaAccountById = useCallback(
    (accountId?: number) => {
      const account = getAccountById(accountId);
      return getIsMicaAccount(account);
    },
    [getAccountById]
  );

  const getActiveAccountsWithSameApis = useCallback(
    (accountId?: number) => {
      const account = getAccountById(accountId);
      return clientsData?.filter((otherAccount) => {
        if (account?.client_id === otherAccount.client_id) return false;
        return (
          otherAccount.client_uuid === account?.client_uuid &&
          otherAccount.client_status === ClientStatus.ACTIVE
        );
      });
    },
    [clientsData]
  );

  const getAccountHasActiveDuplicate = useCallback(
    (accountId?: number) => {
      const account = getAccountById(accountId);
      return !!clientsData?.some((otherAccount) => {
        if (account?.client_id === otherAccount.client_id) return false;
        return (
          otherAccount.client_unique_identifier ===
            account?.client_unique_identifier &&
          otherAccount.client_status === ClientStatus.ACTIVE
        );
      });
    },
    [getAccountStatus]
  );

  const getAccountIsReadOnly = useCallback(
    (accountId?: number) => {
      const account = getAccountById(accountId);
      return (
        account?.permission_level === UserImpersonationPermissionLevel.READ
      );
    },
    [getAccountById]
  );

  const contextValue = useMemo(() => {
    return {
      getClientsListStatus,
      clientsData,
      clientIds,
      clientExchanges,
      getAccountById,
      getAccountByName,
      getAccountExchangeById,
      getIsAccountInvalid,
      getIsAccountInactive,
      getAccountStatus,
      getAccountHasActiveDuplicate,
      activeClientIds,
      activeClientsData,
      writableActiveClientsData,
      getActiveAccountsWithSameApis,
      notDeletedClientsData,
      getIsMicaAccount: getIsMicaAccountById,
      isSuccess,
      getAccountIsReadOnly,
    };
  }, [clientsData, getClientsListStatus]);

  return (
    <ClientsDataContext.Provider value={contextValue}>
      {children}
    </ClientsDataContext.Provider>
  );
};

export default React.memo(ClientsDataProvider);
